<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      @keydown.esc="close"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-[#140f23]/55 backdrop-blur-sm"
        @click="close"
      />

      <!-- Modal -->
      <div
        class="relative z-10 w-full max-w-2xl overflow-hidden rounded-3xl border border-white/70 bg-white shadow-[0_30px_90px_-32px_rgba(27,14,48,0.45)]"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('share.title')"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-[#f7f2ff] via-[#fff7fb] to-[#fffaf4] px-6 py-4"
        >
          <h2 class="text-xl font-extrabold tracking-tight text-[#2e234b]">
            {{ $t("share.title") }}
          </h2>
          <button
            class="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200"
            @click="close"
          >
            <svg
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-100 px-6 py-4">
          <div class="inline-flex rounded-xl bg-gray-100 p-1">
            <button
              :class="[
                'rounded-lg px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200',
                activeTab === 'share'
                  ? 'bg-white text-[#2e234b] shadow-sm'
                  : 'text-gray-500 hover:text-[#2e234b]',
              ]"
              :aria-pressed="activeTab === 'share'"
              @click="activeTab = 'share'"
            >
              {{ $t("share.shareTab") }}
            </button>
            <button
              :class="[
                'rounded-lg px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200',
                activeTab === 'embed'
                  ? 'bg-white text-[#2e234b] shadow-sm'
                  : 'text-gray-500 hover:text-[#2e234b]',
              ]"
              :aria-pressed="activeTab === 'embed'"
              @click="activeTab = 'embed'"
            >
              {{ $t("share.embedTab") }}
            </button>
          </div>
        </div>

        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="translate-y-1 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="translate-y-0 opacity-100"
          leave-to-class="translate-y-1 opacity-0"
        >
          <div
            v-if="toastVisible"
            class="pointer-events-none absolute right-4 top-[74px] z-20 rounded-lg bg-[#2e234b] px-3 py-2 text-xs font-semibold text-white shadow-lg"
          >
            {{ toastMessage }}
          </div>
        </Transition>

        <!-- Content -->
        <div class="max-h-[72vh] overflow-y-auto px-6 py-5">
          <!-- Share Tab -->
          <div v-if="activeTab === 'share'" class="space-y-4">
            <!-- Link language -->
            <div
              v-if="availableLocales.length > 1"
              class="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5"
            >
              <label
                for="share-link-language"
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                {{ $t("share.linkLanguageLabel") }}
              </label>
              <select
                id="share-link-language"
                v-model="selectedShareLocale"
                class="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700 outline-none transition-colors focus:border-pink-400 focus:ring-2 focus:ring-pink-100"
              >
                <option
                  v-for="loc in availableLocales"
                  :key="loc.code"
                  :value="loc.code"
                >
                  {{ loc.name }}
                </option>
              </select>
              <p class="mt-1.5 text-[11px] text-gray-500">
                {{ $t("share.linkLanguageHint") }}
              </p>
            </div>

            <!-- Preview Card -->
            <SharePreviewCard
              :title="title"
              :description="description"
              :image-url="imageUrl"
              :url="shareUrl"
              :type="embedType"
              :date="date"
              :location="location"
              :price="price"
            />

            <!-- Copy Link -->
            <div
              class="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5"
            >
              <span class="flex-1 truncate text-sm text-gray-600">{{
                shareUrl
              }}</span>
              <button
                class="flex-shrink-0 rounded-lg bg-[#2e234b] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#3d3060] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200"
                @click="copyLink"
              >
                {{ copied ? $t("share.copied") : $t("share.copyLink") }}
              </button>
            </div>

            <!-- QR Download -->
            <div
              class="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-[#fff8fc] p-4"
            >
              <p
                class="text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                {{ $t("share.qrTitle") }}
              </p>
              <p class="mt-1 text-[11px] text-gray-500">
                {{ $t("share.qrDescription") }}
              </p>

              <div
                class="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
              >
                <label
                  for="qr-format"
                  class="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                >
                  {{ $t("share.qrFormatLabel") }}
                </label>
                <select
                  id="qr-format"
                  v-model="selectedQrFormat"
                  class="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700 outline-none transition-colors focus:border-pink-400 focus:ring-2 focus:ring-pink-100"
                >
                  <option value="a6">{{ $t("share.qrFormatA6") }}</option>
                  <option value="a5">{{ $t("share.qrFormatA5") }}</option>
                </select>
              </div>

              <div
                class="mt-3 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:flex-row sm:items-center"
              >
                <div
                  class="mx-auto flex h-28 w-28 items-center justify-center rounded-md border border-gray-200 bg-white sm:mx-0"
                >
                  <img
                    v-if="qrDataUrl"
                    :src="qrDataUrl"
                    :alt="$t('share.qrCodeAlt')"
                    class="h-24 w-24"
                  />
                  <span v-else class="text-[10px] text-gray-400">{{
                    $t("share.loadingQr")
                  }}</span>
                </div>

                <div class="min-w-0 flex-1">
                  <span
                    class="text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                    >{{ $t("share.qrLabel") }}</span
                  >
                  <p
                    class="truncate text-base font-bold tracking-[0.12em] text-[#2e234b]"
                  >
                    {{ qrBadgeText }}
                  </p>
                  <p class="mt-0.5 truncate text-[11px] text-gray-500">
                    {{ props.title }}
                  </p>
                </div>
              </div>

              <button
                :disabled="!qrDataUrl"
                class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-[#2e234b] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#3d3060] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200 disabled:cursor-not-allowed disabled:opacity-60"
                @click="downloadQrCode"
              >
                <svg
                  class="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {{ $t("share.downloadQr") }}
              </button>
            </div>

            <!-- Social Buttons -->
            <div class="grid grid-cols-3 gap-2 sm:grid-cols-6">
              <a
                v-for="channel in socialChannels"
                :key="channel.name"
                :href="channel.href"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex flex-col items-center gap-1.5 rounded-xl border border-transparent p-3 text-center transition-all hover:border-gray-200 hover:bg-gray-50"
                :title="channel.name"
              >
                <div
                  class="flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-transform group-hover:scale-105"
                  :style="{ backgroundColor: channel.bg }"
                >
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span class="text-lg" v-html="channel.icon" />
                </div>
                <span class="text-[11px] text-gray-500">{{
                  channel.name
                }}</span>
              </a>
            </div>

            <!-- Native Share (mobile) -->
            <button
              v-if="hasNativeShare"
              class="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200"
              @click="nativeShare"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              {{ $t("share.moreOptions") }}
            </button>
          </div>

          <!-- Embed Tab -->
          <div v-if="activeTab === 'embed'" class="space-y-3">
            <p class="mb-3 text-sm text-gray-500">
              {{ $t("share.embedDescription") }}
            </p>
            <div class="rounded-xl border border-gray-200 bg-gray-900 p-4">
              <code
                class="block whitespace-pre-wrap break-all text-xs text-green-400"
                >{{ embedSnippet }}</code
              >
            </div>
            <button
              class="flex w-full items-center justify-center gap-2 rounded-lg bg-[#2e234b] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#3d3060] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-200"
              @click="copyEmbed"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              {{ embedCopied ? $t("share.copied") : $t("share.copyCode") }}
            </button>

            <!-- Embed Preview -->
            <div
              class="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4"
            >
              <span
                class="mb-2 block text-[10px] font-medium uppercase tracking-wider text-gray-400"
                >{{ $t("share.preview") }}</span
              >
              <SharePreviewCard
                :title="title"
                :description="description"
                :image-url="imageUrl"
                :url="url"
                :type="embedType"
                :date="date"
                :location="location"
                :price="price"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import QRCode from "qrcode";
import SharePreviewCard from "./SharePreviewCard.vue";

const props = defineProps<{
  show: boolean;
  title: string;
  description: string;
  imageUrl: string;
  url: string;
  date?: string;
  location?: string;
  price?: string;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();

type LocaleOption = {
  code: string;
  name: string;
};

type QrPrintFormat = "a6" | "a5";

const { t, locale, locales } = useI18n();
const { SITE_URL } = usePublicUrls();
const nuxtApp = useNuxtApp();

const activeTab = ref<"share" | "embed">("share");
const copied = ref(false);
const embedCopied = ref(false);
const qrDataUrl = ref("");
const selectedQrFormat = ref<QrPrintFormat>("a6");
const toastVisible = ref(false);
const toastMessage = ref("");
const selectedShareLocale = ref(String(locale.value || "en"));

let toastTimer: ReturnType<typeof setTimeout> | null = null;

const embedType = computed<"event" | "establishment">(() => {
  return props.url.includes("/event/") ? "event" : "establishment";
});

const availableLocales = computed<LocaleOption[]>(() => {
  const raw =
    (locales.value as Array<string | { code: string; name?: string }>) ?? [];
  const parsed = raw
    .map((loc) => {
      if (typeof loc === "string") {
        const code = loc.trim();
        if (!code) return null;
        return { code, name: code.toUpperCase() };
      }

      const code = String(loc?.code ?? "").trim();
      if (!code) return null;

      const name = String(loc?.name ?? "").trim() || code.toUpperCase();
      return { code, name };
    })
    .filter((loc): loc is LocaleOption => !!loc);

  if (parsed.length > 0) return parsed;
  const fallback = String(locale.value || "en");
  return [{ code: fallback, name: fallback.toUpperCase() }];
});

const defaultShareLocale = computed(() => {
  const configuredDefault = String(
    (nuxtApp.$i18n as { defaultLocale?: string } | undefined)?.defaultLocale ??
      "",
  ).trim();
  if (
    configuredDefault &&
    availableLocales.value.some((loc) => loc.code === configuredDefault)
  ) {
    return configuredDefault;
  }

  if (availableLocales.value.some((loc) => loc.code === "en")) return "en";
  return availableLocales.value[0]?.code ?? String(locale.value || "en");
});

const effectiveShareLocale = computed(() => {
  if (
    availableLocales.value.some((loc) => loc.code === selectedShareLocale.value)
  ) {
    return selectedShareLocale.value;
  }
  return defaultShareLocale.value;
});

function localizeShareUrl(inputUrl: string, localeCode: string): string {
  try {
    const url = new URL(inputUrl, `${SITE_URL}/`);
    const localeCodes = availableLocales.value.map((loc) => loc.code);
    const rawSegments = url.pathname.split("/").filter(Boolean);
    const pathWithoutLocale = localeCodes.includes(rawSegments[0] ?? "")
      ? rawSegments.slice(1)
      : rawSegments;

    const localizedSegments =
      localeCode === defaultShareLocale.value
        ? pathWithoutLocale
        : [localeCode, ...pathWithoutLocale];

    url.pathname =
      localizedSegments.length > 0 ? `/${localizedSegments.join("/")}` : "/";
    return url.toString();
  } catch {
    return inputUrl;
  }
}

const shareUrl = computed(() =>
  localizeShareUrl(props.url, effectiveShareLocale.value),
);

const qrBadgeText = computed(() => {
  const base = props.title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  const padded = `${base}upfest000000`;
  return padded.slice(0, 6).toUpperCase();
});

const qrPayload = computed(() => {
  const slugTag = `upf-${qrBadgeText.value}`;
  try {
    const url = new URL(shareUrl.value);
    url.hash = slugTag;
    return url.toString();
  } catch {
    return `${shareUrl.value}#${slugTag}`;
  }
});

async function generateQrCode() {
  if (!import.meta.client) return;
  try {
    qrDataUrl.value = await QRCode.toDataURL(qrPayload.value, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: "M",
      color: {
        dark: "#1f2937",
        light: "#ffffff",
      },
    });
  } catch {
    qrDataUrl.value = "";
  }
}

function sanitizeFileName(input: string): string {
  const cleaned = input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
  return cleaned || "upfest-share";
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function downloadQrCode() {
  if (!import.meta.client || !qrDataUrl.value) return;

  try {
    const formatConfig: Record<
      QrPrintFormat,
      {
        width: number;
        height: number;
        slugSize: number;
        titleSize: number;
        hostSize: number;
      }
    > = {
      a6: {
        width: 1240,
        height: 1748,
        slugSize: 76,
        titleSize: 36,
        hostSize: 28,
      },
      a5: {
        width: 1748,
        height: 2480,
        slugSize: 100,
        titleSize: 46,
        hostSize: 34,
      },
    };

    const selectedFormat = formatConfig[selectedQrFormat.value];
    const canvas = document.createElement("canvas");
    canvas.width = selectedFormat.width;
    canvas.height = selectedFormat.height;

    const context = canvas.getContext("2d");
    if (!context) return;

    const horizontalPadding = Math.round(canvas.width * 0.1);
    const slugY = Math.round(canvas.height * 0.1);
    const titleY = Math.round(canvas.height * 0.15);
    const qrSize = Math.round(canvas.width * 0.72);
    const qrX = Math.round((canvas.width - qrSize) / 2);
    const qrY = Math.round(canvas.height * 0.22);
    const hostY = qrY + qrSize + Math.round(canvas.height * 0.08);

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = "#2e234b";
    context.textAlign = "center";
    context.font = `700 ${selectedFormat.slugSize}px sans-serif`;
    context.fillText(qrBadgeText.value, canvas.width / 2, slugY);

    context.fillStyle = "#6b7280";
    context.font = `400 ${selectedFormat.titleSize}px sans-serif`;
    context.fillText(
      props.title.slice(0, 52),
      canvas.width / 2,
      titleY,
      canvas.width - horizontalPadding * 2,
    );

    const qrImage = await loadImage(qrDataUrl.value);
    context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

    context.fillStyle = "#9ca3af";
    context.font = `400 ${selectedFormat.hostSize}px sans-serif`;
    context.fillText(new URL(shareUrl.value).host, canvas.width / 2, hostY);

    const href = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = href;
    link.download = `${sanitizeFileName(qrBadgeText.value)}-qr-${selectedQrFormat.value}.png`;
    link.click();
    showToast(t("share.toastQrDownloaded"));
  } catch {
    // noop
  }
}

function showToast(message: string) {
  toastMessage.value = message;
  toastVisible.value = true;

  if (toastTimer) {
    clearTimeout(toastTimer);
  }

  toastTimer = setTimeout(() => {
    toastVisible.value = false;
  }, 1800);
}

function close() {
  emit("update:show", false);
}

// Lock body scroll while open
watch(
  () => props.show,
  (open) => {
    if (import.meta.client) {
      document.body.style.overflow = open ? "hidden" : "";
    }

    if (open) {
      activeTab.value = "share";
      selectedShareLocale.value = String(
        locale.value || defaultShareLocale.value,
      );
      void generateQrCode();
    }
  },
);

watch(
  () => [shareUrl.value, qrBadgeText.value],
  () => {
    if (!props.show || !import.meta.client) return;
    void generateQrCode();
  },
);

watch(
  () => locale.value,
  (nextLocale) => {
    if (props.show) return;
    selectedShareLocale.value = String(nextLocale || selectedShareLocale.value);
  },
);

// Copy link
async function copyLink() {
  if (!import.meta.client) return;
  try {
    await navigator.clipboard.writeText(shareUrl.value);
    copied.value = true;
    showToast(t("share.toastLinkCopied"));
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch {
    /* clipboard not available */
  }
}

// Social channels
const shareText = computed(() => `${props.title} — ${t("share.checkItOut")}`);

const socialChannels = computed(() => {
  const encodedUrl = encodeURIComponent(shareUrl.value);
  const encodedText = encodeURIComponent(shareText.value);
  return [
    {
      name: "WhatsApp",
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      bg: "#25D366",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.49a.75.75 0 00.918.918l4.456-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.389 0-4.636-.838-6.397-2.302l-.367-.297-2.647.888.888-2.647-.297-.367A9.934 9.934 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>',
    },
    {
      name: "Telegram",
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      bg: "#0088cc",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>',
    },
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      bg: "#000000",
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      bg: "#1877F2",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      bg: "#0A66C2",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
    },
    {
      name: "Email",
      href: `mailto:?subject=${encodedText}&body=${encodedText}%0A%0A${encodedUrl}`,
      bg: "#6B7280",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>',
    },
  ];
});

// Native share
const hasNativeShare = computed(() => import.meta.client && !!navigator.share);

async function nativeShare() {
  if (!import.meta.client || !navigator.share) return;
  try {
    await navigator.share({
      title: props.title,
      text: shareText.value,
      url: shareUrl.value,
    });
  } catch {
    /* user cancelled */
  }
}

// Embed snippet
const embedSnippet = computed(() => {
  return `<script src="${SITE_URL}/embed/upfest-embed.js" defer><\\/script>\n<upfest-embed src="${props.url}"></upfest-embed>`;
});

// Copy embed code
async function copyEmbed() {
  if (!import.meta.client) return;
  try {
    await navigator.clipboard.writeText(embedSnippet.value);
    embedCopied.value = true;
    showToast(t("share.toastEmbedCopied"));
    setTimeout(() => {
      embedCopied.value = false;
    }, 2000);
  } catch {
    /* clipboard not available */
  }
}
</script>
